import React, { useState } from "react";
import { ChevronDown, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";
import { ResolveAction } from "../../types";

interface ResolveIssueModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { actionTaken: ResolveAction; note: string }) => void;
}

const ACTION_OPTIONS: ResolveAction[] = [
  "Mark as valid",
  "Funds reversed",
  "Wallet frozen",
  "Report dismissed",
];

export const ResolveIssueModal: React.FC<ResolveIssueModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const [selectedAction, setSelectedAction] = useState<ResolveAction | "">("");
  const [note, setNote] = useState("");

  const handleClose = () => {
    onOpenChange(false);
    setSelectedAction("");
    setNote("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAction) return;
    onSubmit({ actionTaken: selectedAction, note });
    handleClose();
  };

  const canSubmit = Boolean(selectedAction);

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[520px] bg-white rounded-[24px]"
    >
      <ModalHeader className="items-center justify-between pb-3 border-b border-[#EBEDF9]">
        <ModalTitle className="text-[20px] font-[600] text-[#161827]">
          Resolve issue
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          onClick={handleClose}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF] transition-colors"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody className="flex flex-col gap-6 py-6">
          {/* Field 1: Action taken */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              Action taken
            </p>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex h-12 w-full items-center justify-between rounded-[10px] bg-[#E9ECF8] px-4 text-left transition-colors hover:bg-[#E2E6F5] focus:outline-none"
                >
                  <span
                    className={`text-[14px] font-[400] truncate ${
                      selectedAction ? "text-[#161827]" : "text-[#737AA8]"
                    }`}
                  >
                    {selectedAction || "Select action"}
                  </span>
                  <ChevronDown size={18} className="text-[#6F739B] shrink-0" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={6}
                  className="z-[125] max-h-[240px] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-[12px] border border-[#E1E6FF] bg-white p-1.5 shadow-lg"
                >
                  {ACTION_OPTIONS.map((action) => {
                    const isSelected = selectedAction === action;
                    return (
                      <DropdownMenu.Item
                        key={action}
                        onSelect={() => setSelectedAction(action)}
                        className={`flex items-center cursor-pointer rounded-[8px] px-3.5 py-2.5 text-[14px] outline-none transition-colors focus:bg-[#F5F6FD] ${
                          isSelected
                            ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                            : "text-[#383B56]"
                        }`}
                      >
                        <span className="truncate">{action}</span>
                      </DropdownMenu.Item>
                    );
                  })}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {/* Field 2: Note • Optional */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              Note <span className="font-normal italic text-[#6F739B]">• Optional</span>
            </p>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add notes"
              className="min-h-[100px] w-full resize-none rounded-[10px] bg-[#E9ECF8] p-4 text-[14px] text-[#161827] placeholder:text-[#9A9CC0] focus:outline-none focus:ring-2 focus:ring-[#D9DEFF]"
            />
          </div>
        </ModalBody>

        <ModalFooter className="flex items-center justify-end border-t border-[#EBEDF9] px-6 py-4">
          <Button
            type="submit"
            disabled={!canSubmit}
            className="h-11 rounded-[10px] px-6 text-[15px] font-[500] bg-[#161827] text-white hover:bg-[#2B2F49] disabled:bg-[#9A9CC0] disabled:opacity-100 disabled:cursor-not-allowed transition-colors"
          >
            Mark as resolved
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
